import { useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Button, Form, Row, Col, Tab, Nav, Badge } from 'react-bootstrap';
import { HiEye, HiEyeOff } from 'react-icons/hi';

import {
    updateUserProfile,
    updateUserUsername,
    updateUserEmail,
    updateUserPassword,
} from '../../store/actions/users';
import { errorHelper } from '../../utils/tools';
import {
    personalInfoValidation,
    usernameValidation,
    emailValidation,
    passwordValidation,
} from './validationSchema';

const Profile = () => {

    const user = useSelector(state => state.users.data);
    const dispatch = useDispatch();

    const [usernameTaken, setUsernameTaken] = useState(false);
    const [emailTaken, setEmailTaken] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Meno, priezvisko, mobil
    const personalInfoForm = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: user?.name || '',
            surname: user?.surname || '',
            contact: user?.contact || '',
        },
        validationSchema: personalInfoValidation,
        onSubmit: (values) => {
            dispatch(updateUserProfile({
                username: user?.username,
                email: user?.email,
                ...values,
            }));
        }
    });

    // Prihlasovacie meno
    const usernameForm = useFormik({
        enableReinitialize: true,
        initialValues: { username: user?.username || '' },
        validationSchema: usernameValidation,
        onSubmit: (values) => {
            dispatch(updateUserUsername(values));
        }
    });

    const handleUsernameBlur = async (e) => {
        usernameForm.handleBlur(e);
        const value = e.target.value;
        if (!value || value.length < 3 || value === user?.username) { setUsernameTaken(false); return; }
        try {
            const res = await axios.get(`/api/auth/check-username?username=${encodeURIComponent(value)}`);
            setUsernameTaken(res.data.taken);
        } catch (_) {}
    };

    // E-mail
    const emailForm = useFormik({
        enableReinitialize: true,
        initialValues: { email: user?.email || '' },
        validationSchema: emailValidation,
        onSubmit: (values) => {
            dispatch(updateUserEmail(values));
        }
    });

    const handleEmailBlur = async (e) => {
        emailForm.handleBlur(e);
        const value = e.target.value;
        if (!value || value.length < 5 || value === user?.email) { setEmailTaken(false); return; }
        try {
            const res = await axios.get(`/api/auth/check-email?email=${encodeURIComponent(value)}`);
            setEmailTaken(res.data.taken);
        } catch (_) {}
    };

    // Heslo
    const passwordForm = useFormik({
        initialValues: { oldPassword: '', password: '', confirmPassword: '' },
        validationSchema: passwordValidation,
        onSubmit: async (values) => {
            try {
                await dispatch(updateUserPassword({ oldPassword: values.oldPassword, password: values.password })).unwrap();
                passwordForm.resetForm();
            } catch (_) {}
        }
    });

    return (
        <div className="content p-4">
            <Tab.Container defaultActiveKey="personal">
                <Nav variant="tabs" className="mb-4">
                    <Nav.Item>
                        <Nav.Link eventKey="personal">Osobné údaje</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="email">E-mail</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="password">Heslo</Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content>

                    {/* OSOBNE UDAJE */}
                    <Tab.Pane eventKey="personal">
                        <Form onSubmit={personalInfoForm.handleSubmit} className="mb-5">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileName">
                                        <Form.Label>Meno*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte meno:"
                                            {...personalInfoForm.getFieldProps('name')}
                                            isInvalid={errorHelper(personalInfoForm, 'name').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(personalInfoForm, 'name').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileSurname">
                                        <Form.Label>Priezvisko*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte priezvisko:"
                                            {...personalInfoForm.getFieldProps('surname')}
                                            isInvalid={errorHelper(personalInfoForm, 'surname').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(personalInfoForm, 'surname').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileContact">
                                        <Form.Label>Mobil* (+421910123456)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte mobil:"
                                            {...personalInfoForm.getFieldProps('contact')}
                                            isInvalid={errorHelper(personalInfoForm, 'contact').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errorHelper(personalInfoForm, 'contact').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">Uložiť zmeny</Button>
                        </Form>

                        <hr className="mb-4" />

                        <Form onSubmit={usernameForm.handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileUsername">
                                        <Form.Label>Prihlasovacie meno*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte prihlasovacie meno:"
                                            {...usernameForm.getFieldProps('username')}
                                            onChange={(e) => { usernameForm.handleChange(e); setUsernameTaken(false); }}
                                            onBlur={handleUsernameBlur}
                                            isInvalid={usernameTaken || errorHelper(usernameForm, 'username').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {usernameTaken ? 'Prihlasovacie meno je už obsadené.' : errorHelper(usernameForm, 'username').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={usernameTaken || usernameForm.values.username === user?.username}
                            >
                                Zmeniť prihlasovacie meno
                            </Button>
                        </Form>
                    </Tab.Pane>

                    {/* EMAIL */}
                    <Tab.Pane eventKey="email">
                        <p className="text-muted mb-3">
                            Aktuálny e-mail: <strong>{user?.email}</strong>{' '}
                            {user?.verified ? (
                                <Badge bg="success">Overený</Badge>
                            ) : (
                                <Badge bg="warning" text="dark">Neoverený</Badge>
                            )}
                        </p>
                        <Form onSubmit={emailForm.handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileEmail">
                                        <Form.Label>Nový e-mail*</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Zadajte e-mail:"
                                            {...emailForm.getFieldProps('email')}
                                            onChange={(e) => { emailForm.handleChange(e); setEmailTaken(false); }}
                                            onBlur={handleEmailBlur}
                                            isInvalid={emailTaken || errorHelper(emailForm, 'email').error}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {emailTaken ? 'E-mail je už obsadený.' : errorHelper(emailForm, 'email').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <p className="text-muted" style={{ fontSize: 13 }}>
                                Po zmene e-mailu bude potrebné váš účet znovu overiť.
                            </p>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={emailTaken || emailForm.values.email === user?.email}
                            >
                                Zmeniť e-mail
                            </Button>
                        </Form>
                    </Tab.Pane>

                    {/* HESLO */}
                    <Tab.Pane eventKey="password">
                        <Form onSubmit={passwordForm.handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileOldPassword">
                                        <Form.Label>Aktuálne heslo*</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showOldPassword ? 'text' : 'password'}
                                                placeholder="Zadajte aktuálne heslo:"
                                                style={{ paddingRight: '2.5rem' }}
                                                {...passwordForm.getFieldProps('oldPassword')}
                                                isInvalid={errorHelper(passwordForm, 'oldPassword').error}
                                            />
                                            <span
                                                onClick={() => setShowOldPassword(p => !p)}
                                                style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d', zIndex: 5 }}
                                            >
                                                {showOldPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                                            </span>
                                        </div>
                                        <Form.Control.Feedback type="invalid" style={{ display: errorHelper(passwordForm, 'oldPassword').error ? 'block' : 'none' }}>
                                            {errorHelper(passwordForm, 'oldPassword').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileNewPassword">
                                        <Form.Label>Nové heslo*</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Zadajte nové heslo:"
                                                style={{ paddingRight: '2.5rem' }}
                                                {...passwordForm.getFieldProps('password')}
                                                isInvalid={errorHelper(passwordForm, 'password').error}
                                            />
                                            <span
                                                onClick={() => setShowPassword(p => !p)}
                                                style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d', zIndex: 5 }}
                                            >
                                                {showPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                                            </span>
                                        </div>
                                        <Form.Control.Feedback type="invalid" style={{ display: errorHelper(passwordForm, 'password').error ? 'block' : 'none' }}>
                                            {errorHelper(passwordForm, 'password').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3" controlId="formProfileConfirmPassword">
                                        <Form.Label>Zopakujte nové heslo*</Form.Label>
                                        <div style={{ position: 'relative' }}>
                                            <Form.Control
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder="Zopakujte nové heslo:"
                                                style={{ paddingRight: '2.5rem' }}
                                                {...passwordForm.getFieldProps('confirmPassword')}
                                                isInvalid={errorHelper(passwordForm, 'confirmPassword').error}
                                            />
                                            <span
                                                onClick={() => setShowConfirmPassword(p => !p)}
                                                style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', cursor: 'pointer', color: '#6c757d', zIndex: 5 }}
                                            >
                                                {showConfirmPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
                                            </span>
                                        </div>
                                        <Form.Control.Feedback type="invalid" style={{ display: errorHelper(passwordForm, 'confirmPassword').error ? 'block' : 'none' }}>
                                            {errorHelper(passwordForm, 'confirmPassword').helperText}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Button variant="primary" type="submit">Zmeniť heslo</Button>
                        </Form>
                    </Tab.Pane>

                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default Profile;
